import {
  WithdrawAmountFormField,
  WithdrawIncludeFeeFormField,
} from '@/entities/operation';
import {
  ApiErrorText,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
  SelectInput,
  SelectItem,
} from '@/shared/components';
import { getRequiredLabel } from '@/shared/lib';
import type { UseFormReturn } from 'react-hook-form';
import { GamingHallLabel, type SccrsFormValues, type SccrsGamingHall } from '@/entities/sccrs';
import { useTranslation } from 'react-i18next';
import type { CurrencyAssetOption } from '@/features/withdraw-sccrs';
import { cn } from '@/shared/utils';

interface WithdrawSccrsFormProps {
  form: UseFormReturn<SccrsFormValues>;
  currencyOptions: CurrencyAssetOption[];
  platformOptions: SccrsGamingHall[];
  selectedAsset?: CurrencyAssetOption;
  hasWithdrawFee: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown | null;
  onMaxClick: () => void;
  handleFormSubmit: (data: SccrsFormValues) => void;
}

export const WithdrawSccrsForm = ({
  form,
  currencyOptions,
  platformOptions,
  handleFormSubmit,
  selectedAsset,
  onMaxClick,
  hasWithdrawFee,
  isError,
  isLoading,
  error,
}: WithdrawSccrsFormProps) => {
  const { t } = useTranslation();

  if (!form) {
    return <div>Form is not available</div>;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ApiErrorText error={error} size="small" />;
  }

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form
          onSubmit={event => {
            event.preventDefault();
            form.handleSubmit(handleFormSubmit)(event);
          }}
          className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('withdrawMethods.sccrs.placeholders.currency'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={currencyOptions}
                      selectedKey="code"
                      valueKey="code"
                      labelKey="name"
                      disabled={true}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="fls"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('withdrawMethods.sccrs.placeholders.fls'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <Input label={label} value={field.value} disabled={true} />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="platform"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('withdrawMethods.sccrs.placeholders.platform'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={platformOptions}
                      customOptionsRender={o => {
                        const option = o as SccrsGamingHall;
                        return (
                          <SelectItem
                            key={`${option.gamingHallId}_${option.gamingHallName}`}
                            value={option.gamingHallId}
                            showOptionCheckmark={false}
                            className="px-6 py-3 h-auto rounded-lg min-h-[6rem] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <h1 className={'headline4 text-primary'}>
                                <GamingHallLabel hall={option} />
                              </h1>
                            </div>
                          </SelectItem>
                        );
                      }}
                      renderSelected={o => {
                        const option = o as SccrsGamingHall;
                        return (
                          <span
                            className={cn(
                              'p3',
                              'w-full min-w-0 truncate block'
                            )}>
                            <GamingHallLabel hall={option} />
                          </span>
                        );
                      }}
                      valueKey="gamingHallId"
                      disabled={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <WithdrawAmountFormField
            control={form.control}
            name="amount"
            selectedAsset={selectedAsset}
            onMaxClick={onMaxClick}
            availableBalanceTranslationKey="withdrawMethods.sccrs.availableBalance"
          />

          <WithdrawIncludeFeeFormField
            control={form.control}
            name="includeFee"
            visible={hasWithdrawFee}
          />
        </form>
      </Form>
    </div>
  );
};
